import { useTranslation } from 'react-i18next';
import { OrganizationPicker } from './OrganizationPicker';
import { ContactPicker } from './ContactPicker';

interface OrganizationContactComboSelectProps {
  organizationId: string;
  contactId: string;
  onCompanyChange: (organizationId: string) => void;
  onContactChange: (contactId: string) => void;
  companyRequired?: boolean;
  contactRequired?: boolean;
  disabled?: boolean;
}

/**
 * A reusable component that provides linked Organization and Contact selection
 * with autocomplete search. When a company is selected, the contact dropdown
 * filters to show only contacts associated with that company.
 *
 * Layout: Organization on the left, Contact on the right (2-column grid).
 */
export function OrganizationContactComboSelect({
  organizationId,
  contactId,
  onCompanyChange,
  onContactChange,
  companyRequired = false,
  contactRequired = false,
  disabled = false,
}: OrganizationContactComboSelectProps) {
  const { t } = useTranslation('contacts');

  const companyLabel = companyRequired
    ? `${t('companyContactCombo.company')} *`
    : t('companyContactCombo.company');

  const handleCompanyChange = (newCompanyId: string) => {
    onCompanyChange(newCompanyId);

    // Clear contact selection when company changes, since the contact
    // may no longer be valid for the new company filter
    if (newCompanyId !== organizationId) {
      onContactChange('');
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <OrganizationPicker
        value={organizationId}
        onChange={handleCompanyChange}
        label={companyLabel}
        disabled={disabled}
        allowClear={!companyRequired}
      />

      <ContactPicker
        value={contactId}
        onChange={onContactChange}
        organizationId={organizationId}
        label={t('companyContactCombo.contact')}
        disabled={disabled}
        required={contactRequired}
        defaultCompanyId={organizationId}
      />
    </div>
  );
}
